import React, { useEffect, useRef, useState } from "react";

const FRAME_WIDTH = 800;
const FRAME_HEIGHT = 600;
const INTERVAL_TIME = 1000;
const POINT_SIZE = 10;
const NUM_ANTS = 5;
const MAX_TRAIL_LENGTH = 20;

class CollisionMark {
  constructor(x, y, antId, edge, timestamp = Date.now()) {
    this.x = x;
    this.y = y;
    this.antId = antId;
    this.edge = edge;
    this.timestamp = timestamp;
  }
}

class Position {
  constructor(x, y, timestamp = Date.now()) {
    this.x = x;
    this.y = y;
    this.timestamp = timestamp;
  }
}

class LivingEntity {
  constructor(x, y, id, world) {
    this.x = x;
    this.y = y;
    this.id = id;
    this.trail = [];
    this.marks = [];
    this.trailIntersections = [];
    this.world = world;
  }

  addToTrail(position) {
    this.trail.push(position);
    if (this.trail.length > MAX_TRAIL_LENGTH) {
      this.trail.shift();
    }
  }

  addToMarks(mark) {
    this.marks.push(mark);
  }

  addToTrailIntersections(position) {
    this.trailIntersections.push(position);
  }

  preconditions(dx, dy) {
    let newPosition = new Position(this.x + dx, this.y + dy);

    if (this.marks.some(mark =>
      Math.abs(mark.x - newPosition.x) < POINT_SIZE &&
      Math.abs(mark.y - newPosition.y) < POINT_SIZE
    )) {
      return false;
    }

    if (this.trail.some(pos =>
      Math.abs(pos.x - newPosition.x) < POINT_SIZE &&
      Math.abs(pos.y - newPosition.y) < POINT_SIZE
    )) {
      return false;
    }

    return true;
  }

  isIntersectedWithTrail() {
    const newPosition = new Position(this.x, this.y);
    const others = this.world.getOtherEntities(this);

    for (const other of others) {
      if (other.trail.some(pos =>
        Math.abs(pos.x - newPosition.x) < POINT_SIZE &&
        Math.abs(pos.y - newPosition.y) < POINT_SIZE
      )) {
        console.warn(`Entity ${this.id} has moved to (${newPosition.x}, ${newPosition.y}) intersecting with ${other.id}`);
        return true;
      }
    }

    return false;
  }

  move() {
    let dx, dy;
    let attemptCount = 0;
    const maxAttempts = 100;

    while (attemptCount < maxAttempts) {
      dx = Math.floor(Math.random() * 21) - POINT_SIZE;
      dy = Math.floor(Math.random() * 21) - POINT_SIZE;

      if (this.preconditions(dx, dy)) break;
      attemptCount++;
    }

    this.x = Math.max(0, Math.min(FRAME_WIDTH - POINT_SIZE, this.x + dx));
    this.y = Math.max(0, Math.min(FRAME_HEIGHT - POINT_SIZE, this.y + dy));

    this.addToTrail(new Position(this.x, this.y));

    let collidedEdge = null;
    if (this.x <= 0) collidedEdge = "left";
    else if (this.x >= FRAME_WIDTH - POINT_SIZE) collidedEdge = "right";
    else if (this.y <= 0) collidedEdge = "top";
    else if (this.y >= FRAME_HEIGHT - POINT_SIZE) collidedEdge = "bottom";

    if (collidedEdge) {
      this.addToMarks(new CollisionMark(this.x, this.y, this.id, collidedEdge));
    }

    if (this.isIntersectedWithTrail()) {
      this.addToTrailIntersections(new Position(this.x, this.y));
    }
  }

  isCollidingWith(other) {
    return (
      Math.abs(this.x - other.x) < POINT_SIZE &&
      Math.abs(this.y - other.y) < POINT_SIZE
    );
  }

  onInteract(other) {
    console.log(`${this.constructor.name} ${this.id} interacted with ${other.constructor.name} ${other.id}`);
  }
}

class AntRobot extends LivingEntity {
  constructor(x, y, id, world) {
    super(x, y, id, world);
  }
}

class World {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.entities = [];
  }

  addEntity(entity) {
    this.entities.push(entity);
  }

  getOtherEntities(entity) {
    return this.entities.filter(e => e.id !== entity.id);
  }

  update() {
    this.entities.forEach(entity => {
      entity.move();
    });

    for (let i = 0; i < this.entities.length; i++) {
      for (let j = i + 1; j < this.entities.length; j++) {
        if (this.entities[i].isCollidingWith(this.entities[j])) {
          this.entities[i].onInteract(this.entities[j]);
          this.entities[j].onInteract(this.entities[i]);
        }
      }
    }
  }
}

export default function CanvasAntSimulation() {
  const worldRef = useRef(new World(FRAME_WIDTH, FRAME_HEIGHT));

  const [isRunning, setIsRunning] = useState(false);
  const [ants, setAnts] = useState(() => {
    const world = worldRef.current;
    const newAnts = Array.from({ length: NUM_ANTS }, (_, i) => {
      const ant = new AntRobot(
        Math.floor(Math.random() * (FRAME_WIDTH - POINT_SIZE)),
        Math.floor(Math.random() * (FRAME_HEIGHT - POINT_SIZE)),
        i,
        world
      );
      world.addEntity(ant);
      return ant;
    });
    return newAnts;
  });

  const canvasRef = useRef(null);

  const updateSimulation = () => {
    worldRef.current.update();
    setAnts([...worldRef.current.entities]);
  };

  useEffect(() => {
    let intervalId;
    if (isRunning) {
      intervalId = setInterval(updateSimulation, INTERVAL_TIME);
    }
    return () => clearInterval(intervalId);
  }, [isRunning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, FRAME_WIDTH, FRAME_HEIGHT);

    ants.forEach((ant) => {
      for (let i = 0; i < ant.trail.length - 1; i++) {
        const a = ant.trail[i];
        const b = ant.trail[i + 1];
        const opacity = ((i + 1) / ant.trail.length) * 0.9;

        ctx.strokeStyle = `rgba(0, 255, 255, ${opacity})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(a.x + POINT_SIZE / 2, a.y + POINT_SIZE / 2);
        ctx.lineTo(b.x + POINT_SIZE / 2, b.y + POINT_SIZE / 2);
        ctx.stroke();
      }

      ant.marks.forEach((mark) => {
        ctx.fillStyle = "limegreen";
        ctx.beginPath();
        ctx.arc(mark.x + POINT_SIZE / 2, mark.y + POINT_SIZE / 2, POINT_SIZE / 3, 0, Math.PI * 2);
        ctx.fill();
      });
    });

    ants.forEach((ant) => {
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(ant.x + POINT_SIZE / 2, ant.y + POINT_SIZE / 2, POINT_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [ants]);

  return (
    <div style={{ textAlign: "center", marginTop: "20px" }}>
      <button
        onClick={() => setIsRunning((r) => !r)}
        style={{ padding: "10px 20px", fontSize: "16px" }}
      >
        {isRunning ? "Stop" : "Start"} Ants
      </button>

      <div
        style={{
          width: FRAME_WIDTH,
          height: FRAME_HEIGHT,
          margin: "20px auto",
          background: "black",
        }}
      >
        <canvas
          ref={canvasRef}
          width={FRAME_WIDTH}
          height={FRAME_HEIGHT}
          style={{ display: "block" }}
        />
      </div>
    </div>
  );
}